import { graphql, useStaticQuery } from "gatsby"
import { useGeneralSeo, type SeoContextValue } from "@/context/SeoContext"
import { SITE_URL } from "@/libs/constants"

type Tag = {
  name?: string
  property?: string
  content: string
}

type SEOMetaProps = {
  data?: SeoContextValue & {
    additionalTags?: Array<Tag>
    imageAlt?: string
  }
  pathname: string
} & (
  | {
      isArticle?: false
      publishedDate?: never
    }
  | {
      isArticle: true
      publishedDate: string
    }
)

const SEOMeta = ({
  data,
  pathname,
  isArticle,
  publishedDate,
}: SEOMetaProps) => {
  const generalSeoData = useGeneralSeo()
  const seoMetaQuery: Queries.SeoMetaStaticQuery = useStaticQuery(graphql`
    query SeoMetaStatic {
      allSanityGeneral {
        nodes {
          generalSettings {
            twitterUsername
            facebookUrl
          }
        }
      }
    }
  `)

  const { twitterUsername, facebookUrl } =
    seoMetaQuery.allSanityGeneral.nodes[0].generalSettings

  const {
    metaTitle,
    metaDescription,
    previewImage,
    isIndexed = true,
    followLinks = true,
    additionalTags = [],
  } = data || generalSeoData || {}

  const previewImageUrl = makeAbsolute(previewImage?.asset?.url) || undefined

  return (
    <>
      {/* Google / Search Engine Tags */}
      {metaTitle && <title>{metaTitle}</title>}
      {metaDescription && <meta name="description" content={metaDescription} />}

      <meta itemProp="name" content={metaTitle} />
      <meta itemProp="description" content={metaDescription} />
      {previewImageUrl && <meta itemProp="image" content={previewImageUrl} />}

      <meta
        name="robots"
        content={`${isIndexed === false ? "noindex, " : "index, "}${
          followLinks === false ? "nofollow, " : "follow, "
        }max-image-preview:large, max-snippet:-1, max-video-preview:-1`}
      />

      <link rel="canonical" href={SITE_URL + pathname} />

      {/* Facebook Meta Tags */}
      {isArticle && (
        <>
          <meta property="article:publisher" content={facebookUrl} />
          <meta property="article:modified_time" content={publishedDate} />
          <meta property="article:published_time" content={publishedDate} />
        </>
      )}

      {metaTitle && <meta property="og:title" content={metaTitle} />}
      {metaDescription && (
        <meta property="og:description" content={metaDescription} />
      )}
      <meta
        property="og:type"
        content={
          additionalTags.find(tag => tag.property === "og:type")?.content ||
          (isArticle ? "article" : "website")
        }
      />
      <meta property="og:url" content={SITE_URL + pathname} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="the DONUT" />
      {previewImageUrl && (
        <>
          <meta property="og:image" content={previewImageUrl} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          {previewImage?.alt && (
            <meta property="og:image:alt" content={previewImage.alt} />
          )}
        </>
      )}

      {/* Twitter Meta Tags */}
      <meta name="twitter:site" content="@donutdailynews" />
      <meta name="twitter:creator" content="@donutdailynews" />

      {previewImageUrl && (
        <>
          <meta name="twitter:image" content={previewImageUrl} />
          {previewImage?.alt && (
            <meta name="twitter:image:alt" content={previewImage.alt} />
          )}
        </>
      )}

      {metaTitle && <meta name="twitter:title" content={metaTitle} />}
      {metaDescription && (
        <meta name="twitter:description" content={metaDescription} />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      {twitterUsername && (
        <meta name="twitter:site" content={twitterUsername} />
      )}

      {/* Handle additional meta tags */}
      {additionalTags.map((tag: Tag, index: number) => {
        // For tags with 'name' attribute
        if (tag.name) {
          return (
            <meta
              key={`${tag.name}-${index}`}
              name={tag.name}
              content={tag.content}
            />
          )
        }
        // For tags with 'property' attribute (like og: tags)
        if (tag.property) {
          return (
            <meta
              key={`${tag.property}-${index}`}
              property={tag.property}
              content={tag.content}
            />
          )
        }
        return null
      })}
    </>
  )
}

export default SEOMeta

function makeAbsolute(url?: string | null) {
  if (!url) return undefined
  if (url.startsWith("/")) {
    return SITE_URL + url
  }
  return url
}
